import {Pallete} from '../../../../../../theme/bluebird';
import {Card, CardContent, CardHeader, Chip, Stack, Typography} from '@mui/material';
import {ComboActions} from '../../../../../../components/combo-actions/combo-actions';
import {translations} from '../../../../../../i18n/translations';
import {FiEdit, FiTrash2} from 'react-icons/fi';
import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AffiliatedUser} from '../../../../../../backend/responses/affiliated-user';
import ActionsShiftForm from './actions-shift-flow/actions-shift-form';
import {EntityShift} from '../../../../../../backend/interfaces/entity-shift';
import {EntityShiftsRepository} from '../../../../../../repositories/entity-shifts';
import moment from 'moment/moment';
import {EntitySocialResponse} from '../../../../../../backend/interfaces/entity-social-response';
import DeleteResourceConfirmationModal from '../../../../../../components/delete-resource-confirmation-modal';
import {useEntity} from '../../../../../../contexts/entity/context';
import {getLocalizedFieldValue} from '../../../../../../utils/functions/get-localized-field-value';

export interface ShiftCardProps {
  shift: EntityShift;
  actionsEnabled: boolean;
  allCollaborators: AffiliatedUser[];
  updateCallback?: () => void;
}

const SocialResponseChip = ({socialResponse}: {socialResponse: EntitySocialResponse}) => (
  <Chip
    key={`social-response-${socialResponse.id}`}
    label={getLocalizedFieldValue(socialResponse, 'abbreviation')}
    sx={{
      height: '20px',
      paddingX: 1,
      color: socialResponse.foregroundColor,
      backgroundColor: socialResponse.backgroundColor,
      borderRadius: '24px',
      fontFamily: 'Roboto',
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: '18px',
      '& .MuiChip-label': {padding: 0},
    }}
  />
);

export const ShiftCard = ({
  shift,
  actionsEnabled,
  updateCallback,
  allCollaborators,
}: ShiftCardProps) => {
  const [editShift, setEditShift] = useState<EntityShift | null>(null);
  const [confirmDeleteShiftModalOpened, setConfirmDeleteShiftModalOpened] =
    useState<boolean>(false);

  const {t} = useTranslation();
  const {state: entityState} = useEntity();

  const startTime = moment(shift.startHour).format('HH:mm');
  const endTime = moment(shift.endHour).format('HH:mm');

  const responsibleUser = useMemo(() => {
    return allCollaborators.find(
      (collaborator) => collaborator.user.id === shift.responsibleUserId
    );
  }, [allCollaborators, shift]);

  const shiftSocialResponses = useMemo(() => {
    return entityState.availableSocialResponses?.filter((socialResponse) =>
      shift.socialResponses?.includes(socialResponse.id)
    );
  }, [entityState.availableSocialResponses, shift.socialResponses]);

  // Styles
  const cardTextContentSX = {
    fontFamily: 'Roboto',
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '18px',
    color: Pallete.gray.base,
    minHeight: '1em',
  };

  const closeCreateShiftModal = () => {
    setEditShift(null);
    updateCallback?.();
  };

  const confirmDeleteShift = () => {
    setConfirmDeleteShiftModalOpened(true);
    updateCallback?.();
  };

  const confirmDeleteShiftModal = () => {
    return (
      <DeleteResourceConfirmationModal
        id="confirm-delete-shift-dialog"
        open={confirmDeleteShiftModalOpened}
        title={t(translations.shifts_page.confirm_delete.title, {name: shift.name})}
        description={t(translations.shifts_page.confirm_delete.copy.title)}
        cannotBeUndoneMessage={t(translations.shifts_page.confirm_delete.copy.subtitle)}
        onCancel={() => setConfirmDeleteShiftModalOpened(false)}
        onConfirm={() => {
          setConfirmDeleteShiftModalOpened(false);
          EntityShiftsRepository.instance.deleteShift(shift.entityId, shift.id!).then((r) => {
            updateCallback?.();
          });
        }}
      />
    );
  };

  return (
    <>
      {confirmDeleteShiftModal()}
      <ActionsShiftForm
        title={t(translations.shifts_page.shift_modal.edit_shift)}
        shiftModalOpened={editShift !== null}
        closeShiftModal={closeCreateShiftModal}
        collaborators={allCollaborators}
        originalShift={editShift!}
        entitySocialResponses={entityState.userEntitySocialResponses}
      />
      <Card
        className="shift-card"
        sx={{
          width: '329px',
          height: '170px',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0px -3px 14px 0px rgba(29, 41, 57, 0.15)',
          borderRadius: '5px',
        }}
      >
        <CardHeader
          title={`${shift.name} - ${shift.abbreviation}`}
          action={
            actionsEnabled && (
              <ComboActions
                className="actions"
                wrapperSX={{
                  color: Pallete.gray.light,
                }}
                triggerSX={{
                  height: '32px',
                  width: '32px',
                  '&:active': {backgroundColor: Pallete.blue.lighter},
                }}
                actions={[
                  {
                    label: t(translations.user_card.actions.edit),
                    icon: {element: FiEdit},
                    callback: () => {
                      setEditShift(shift);
                    },
                  },
                  {
                    label: t(translations.user_card.actions.delete),
                    icon: {element: FiTrash2},
                    callback: () => {
                      confirmDeleteShift();
                    },
                  },
                ]}
              />
            )
          }
          sx={{
            padding: 0,
            '& .MuiSvgIcon-root': {
              color: Pallete.gray.light,
            },
            '& .MuiCardHeader-title': {
              fontFamily: 'Roboto',
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: '18px',
              color: Pallete.gray.base,
            },
          }}
        />
        <CardContent
          sx={{
            padding: 0,
          }}
        >
          <Typography sx={cardTextContentSX} mt={1.5}>
            {t(translations.shifts_page.schedule)} {startTime} {t(translations.shifts_page.to_hour)}{' '}
            {endTime}
          </Typography>
          <Typography sx={cardTextContentSX} mt={3}>
            {shift.maxShiftMembers}{' '}
            {shift.maxShiftMembers === 1
              ? t(translations.shifts_page.professionals.singular)
              : t(translations.shifts_page.professionals.plural)}
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
            <Typography sx={cardTextContentSX} mt={3}>
              {t(translations.shifts_page.responsible.label)}:{' '}
              {responsibleUser
                ? responsibleUser.user.displayName || responsibleUser.user.name
                : t(translations.shifts_page.responsible.not_defined)}
            </Typography>
            <Stack direction="row" flexShrink={0} gap={0.5}>
              {shiftSocialResponses?.map((socialResponse) => (
                <SocialResponseChip key={socialResponse.id} socialResponse={socialResponse} />
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};
